'use client';

import Link from 'next/link';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback } from 'react';

const collections = [
    {
        id: 1,
        title: 'Luxury Living',
        description: 'Exclusive villas and penthouses for the elite',
        image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80',
        link: '/buy?type=villa&badge=luxury',
        size: 'large'
    },
    {
        id: 2,
        title: "Nature's Lap",
        description: 'Eco-friendly homes surrounded by greenery',
        image: 'https://images.unsplash.com/photo-1449844908441-8829872d2607?w=800&q=80',
        link: '/buy?badge=eco-friendly',
        size: 'small'
    },
    {
        id: 3,
        title: 'Urban Pulse',
        description: 'Modern apartments in the heart of the city',
        image: 'https://images.unsplash.com/photo-1460317442991-0ec209397118?w=800&q=80',
        link: '/buy?type=apartment&loc=city',
        size: 'small'
    },
    {
        id: 4,
        title: 'Coastal Paradise',
        description: 'Breathtaking homes with stunning ocean views',
        image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80',
        link: '/buy?badge=coastal',
        size: 'small'
    },
    {
        id: 5,
        title: 'Smart Tech Homes',
        description: 'Future-ready properties with advanced automation',
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80',
        link: '/buy?badge=smart-home',
        size: 'small'
    }
];

const CuratedCollections = () => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        slidesToScroll: 1,
        dragFree: true,
        containScroll: 'trimSnaps'
    });

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    return (
        <section className="py-4">
            <div className="container-custom">
                <div className="section-title mb-8">
                    <h2> Civil Services </h2>
                    <p>Handpicked selections for every lifestyle</p>
                </div>

                <div className="relative group">
                    <div className="overflow-hidden" ref={emblaRef}>
                        <div className="flex -ml-6 py-4">
                            {collections.map((item) => (
                                <div
                                    key={item.id}
                                    className={`flex-[0_0_80%] md:flex-[0_0_45%] lg:flex-[0_0_30%] min-w-0 pl-6 h-[350px]`}
                                >
                                    <Link
                                        href={item.link}
                                        className="group/item relative h-full w-full block rounded-2xl overflow-hidden cursor-pointer"
                                    >
                                        {/* Background Image */}
                                        <div className="absolute inset-0">
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover/item:scale-110"
                                            />
                                            {/* Gradient Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover/item:opacity-90 transition-opacity" />
                                        </div>

                                        {/* Content */}
                                        <div className="absolute bottom-0 left-0 p-8 w-full transform translate-y-2 group-hover/item:translate-y-0 transition-transform duration-500">
                                            <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                                            <p className="text-white/80 text-sm mb-4 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover/item:translate-y-0">
                                                {item.description}
                                            </p>
                                            <div className="inline-flex items-center gap-2 text-white font-medium text-sm border-b border-white/30 pb-1 group-hover/item:border-white transition-colors">
                                                Explore
                                                <svg className="w-4 h-4 transform group-hover/item:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                </svg>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <button
                        onClick={scrollPrev}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center text-gray-600 hover:text-primary hover:border-primary transition-all opacity-0 group-hover:opacity-100 z-20 hidden lg:flex"
                        aria-label="Previous slide"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        onClick={scrollNext}
                        className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center text-gray-600 hover:text-primary hover:border-primary transition-all opacity-0 group-hover:opacity-100 z-20 hidden lg:flex"
                        aria-label="Next slide"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        </section>
    );
};

export default CuratedCollections;
